import axios, { AxiosError } from 'axios';
import { auth } from '../firebase'; // your firebase.ts file
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import type { Product, CreateProductInput } from "../types/commerce";

interface ApiError {
  message?: string;
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

export type { Product, CreateProductInput } from "../types/commerce";

export const get_all_products = async (
  page: number = 1,
  limit: number = 20,
  search: string = ""
) => {
  try {
    const response = await backendAPI.get(`/products/all`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || 'Failed to fetch products');
  }
};



export const create_product = async (userBusinessId: number, payload: CreateProductInput) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    let imageUrl: string | undefined;

    if (payload.image && payload.image instanceof File) {
      const fileRef = ref(storage, `products/${Date.now()}-${payload.image.name}`);
      await uploadBytes(fileRef, payload.image);
      imageUrl = await getDownloadURL(fileRef);
    } else if (typeof payload.image === "string") {
      // If it's already a URL, just use it
      imageUrl = payload.image;
    }
    const response = await backendAPI.post(
      `products/create/`,
      {
        userBusinessId,
        title: payload.title,
        description: payload.description,
        price: payload.price,
        quantity: payload.quantity,
        image: imageUrl,
        weight: payload.weight ?? null,
        weightUnit: payload.weightUnit ?? null,
        length: payload.length ?? null,
        width: payload.width ?? null,
        height: payload.height ?? null,
        dimensionUnit: payload.dimensionUnit ?? null,
        ...(payload.categoryId ? { categoryId: payload.categoryId } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

export const update_product = async (productId: number, userBusinessId: number, payload: Product) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    let imageUrl: string | undefined;

    if (payload.image && payload.image instanceof File) {
      const fileRef = ref(storage, `products/${Date.now()}-${payload.image.name}`);
      await uploadBytes(fileRef, payload.image);
      imageUrl = await getDownloadURL(fileRef);
    } else if (typeof payload.image === "string") {
      imageUrl = payload.image;
    }

    const response = await backendAPI.put(
      `products/update/${productId}`,
      {
        userBusinessId,
        title: payload.title,
        description: payload.description,
        price: payload.price,
        quantity: payload.quantity,
        image: imageUrl,
        weight: payload.weight ?? null,
        weightUnit: payload.weightUnit ?? null,
        length: payload.length ?? null,
        width: payload.width ?? null,
        height: payload.height ?? null,
        dimensionUnit: payload.dimensionUnit ?? null,
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  }
};

export const delete_product = async (userBusinessId: number, productId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await backendAPI.delete(
      `products/delete/${productId}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        data: {
          userBusinessId, // Pass business ID in request body for ownership validation
        },
      }
    );

    return response;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};


export const get_user_business_product_types = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();

    const response = await backendAPI.get(
      `products/types/user_business/${businessId}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        }
      }
    ); // Adjust the endpoint URL
    return response; // Return the response data, assuming you want to handle it elsewhere
  } catch (error: any) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Get products types failed');
  }
};

export const get_user_business_products = async (businessId: number) => {
  try {

    const response = await backendAPI.get(
      `products/business_products/${businessId}`,
    )
    return response.data;
  } catch (error: any) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Get products types failed');
  }
};

export const get_user_business_product = async (businessId: number, productId: number) => {
  try {
    const response = await backendAPI.get(
      `products/business_product/${businessId}/${productId}`,
    );
    return response;
  } catch (error: any) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Get products types failed');
  }
};

export const add_to_cart = async (businessId: number, productId: number, quantity: number = 1) => {

  try {
    const headers: Record<string, string> = {};

    const user = auth.currentUser;
    if (user) {
      const idToken = await user.getIdToken();
      headers.Authorization = `Bearer ${idToken}`;
    } else {
      console.warn("[add_to_cart] No authenticated user. Proceeding as guest.");
    }
    console.log("[add_to_cart] Adding to cart:", { businessId, productId, quantity });
    const response = await backendAPI.post(
      "/products/cart",
      { businessId, productId, quantity },
      { headers, withCredentials: true }
    );

    return response.data;
  } catch (error: any) {
    console.error("[add_to_cart] Error details:", {
      message: error?.message,
      response: error?.response,
      stack: error?.stack,
    });

    throw new Error(
      error?.response?.data?.message || "Unexpected error adding to cart"
    );
  }
};

export const get_cart = async (businessId: number) => {
  try {
    const headers: Record<string, string> = {};
    const user = auth.currentUser;

    if (user) {
      const idToken = await user.getIdToken();
      headers.Authorization = `Bearer ${idToken}`;
    }

    const response = await backendAPI.get("/products/cart", {
      params: { businessId },
      headers,
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error("Error getting cart:", error);
    throw error;
  }
};


export const delete_from_cart = async (
  productId: number,
  businessId: number
) => {
  try {
    const headers: Record<string, string> = {};
    const user = auth.currentUser;

    if (user) {
      const idToken = await user.getIdToken();
      headers.Authorization = `Bearer ${idToken}`;
    }

    const response = await backendAPI.delete(
      `/products/cart/${productId}`,
      {
        data: {
          businessId,
        },
        headers,
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting product from cart:", error);
    throw error;
  }
};

export const clear_cart = async (businessId: number) => {
  try {
    const headers: Record<string, string> = {};
    const user = auth.currentUser;

    if (user) {
      const idToken = await user.getIdToken();
      headers.Authorization = `Bearer ${idToken}`;
    }

    const response = await backendAPI.delete('/products/cart', {
      params: { businessId },
      headers,
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error('Error clearing cart:', error);
    throw error;
  }
};

